/**
 * @file
 * The results filter (berTags.components.filterBar): type and status.
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { components } = berTags;
  const { el, iconButton, onClick, setHidden } = berTags.dom;
  const { collator } = berTags.util;

  // A filter toggle + selects panel for popovers whose items carry a type
  // or bundle, plus a publication status select when the results mix
  // statuses. Each select only renders with 2+ choices and the whole
  // control hides while nothing is filterable.
  // An optional store (util.store instance) persists the selections
  // ('filterType'/'filterStatus') and the panel state ('filterOpen').
  components.filterBar = ({ id, onChange, store }) => {
    const saved = store ? store.read() : {};
    const value = {
      type: typeof saved.filterType === 'string' ? saved.filterType : '',
      status: typeof saved.filterStatus === 'string' ? saved.filterStatus : '',
    };
    const panel = el('div', 'ber-tags__filters');
    panel.id = `${id || 'ber'}-filters`;
    const toggle = iconButton(
      'ber-tags__sort-option ber-tags__filter-toggle',
      Drupal.t('Filter'),
      'filter',
    );
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', panel.id);
    setHidden(toggle, true);

    // setAvailable(false) force-hides the control (e.g. server-filtered
    // lists whose current results hold a single type); an engaged filter
    // keeps it visible so the user can always clear it.
    let available = true;

    const sync = () => {
      const engaged = value.type !== '' || value.status !== '';
      // Active while the panel is open or a filter is engaged.
      toggle.classList.toggle(
        'is-active',
        engaged || panel.classList.contains('is-open'),
      );
      const usable =
        (available || engaged) &&
        panel.querySelector('.ber-tags__filter:not(.is-hidden)') !== null;
      setHidden(toggle, !usable);
      if (!usable) {
        panel.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      }
    };

    const selects = {};
    const makeSelect = (key, label, allLabel) => {
      const wrap = el('label', 'ber-tags__filter');
      const text = el('span', 'ber-tags__filter-label');
      text.textContent = label;
      const select = el('select', 'ber-tags__filter-select');
      select.setAttribute('aria-label', label);
      select.addEventListener('change', () => {
        value[key] = select.value;
        if (store) {
          store.write(
            key === 'type' ? 'filterType' : 'filterStatus',
            select.value,
          );
        }
        sync();
        if (onChange) {
          onChange({ ...value });
        }
      });
      wrap.append(text, select);
      setHidden(wrap, true);
      panel.appendChild(wrap);
      selects[key] = { wrap, select, allLabel, signature: null };
    };
    makeSelect('type', Drupal.t('Type'), Drupal.t('- Any -'));
    makeSelect('status', Drupal.t('Status'), Drupal.t('- Any -'));

    // list: [{value, label}]. Rebuilds only when the choices changed;
    // a selection no longer offered silently falls back to all.
    const setOptions = (key, list) => {
      const entry = selects[key];
      const signature = list
        .map((option) => `${option.value}${option.label}`)
        .join('');
      if (signature !== entry.signature) {
        entry.signature = signature;
        entry.select.textContent = '';
        const all = el('option');
        all.value = '';
        all.textContent = entry.allLabel;
        entry.select.appendChild(all);
        list.forEach((option) => {
          const node = el('option');
          node.value = option.value;
          node.textContent = option.label;
          entry.select.appendChild(node);
        });
        if (value[key] && !list.some((option) => option.value === value[key])) {
          value[key] = '';
        }
        entry.select.value = value[key];
        setHidden(entry.wrap, list.length < 2);
      }
      sync();
    };

    const distinct = (metas, key) => {
      const seen = new Set();
      metas.forEach((meta) => {
        if (meta && meta[key]) {
          seen.add(meta[key]);
        }
      });
      return [...seen]
        .sort(collator.compare)
        .map((label) => ({ value: label, label }));
    };

    const reset = () => {
      value.type = '';
      value.status = '';
      selects.type.select.value = '';
      selects.status.select.value = '';
      sync();
    };

    const setPanelOpen = (open) => {
      panel.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (store) {
        store.write('filterOpen', open);
      }
      // Collapsing the panel also clears an engaged filter.
      if (!open && (value.type !== '' || value.status !== '')) {
        reset();
        if (store) {
          store.write('filterType', '');
          store.write('filterStatus', '');
        }
        if (onChange) {
          onChange({ ...value });
        }
      }
      sync();
    };

    onClick(toggle, (event) => {
      event.stopPropagation();
      setPanelOpen(!panel.classList.contains('is-open'));
    });

    if (saved.filterOpen) {
      panel.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }

    return {
      toggle,
      panel,
      value,
      setTypes: (list) => setOptions('type', list),
      setStatuses: (list) => setOptions('status', list),
      setAvailable: (on) => {
        available = on !== false;
        sync();
      },
      closePanel: () => {
        if (panel.classList.contains('is-open')) {
          setPanelOpen(false);
        }
      },
      // Derives both choice lists from row metas ({type, status}).
      update: (metas) => {
        setOptions('type', distinct(metas, 'type'));
        setOptions('status', distinct(metas, 'status'));
      },
      matches: (meta) =>
        (value.type === '' || meta.type === value.type) &&
        (value.status === '' || meta.status === value.status),
      reset,
    };
  };
})(Drupal);
